import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

// Icons
import { ArrowRight, ArrowLeft, Camera, Trash, ImagePlus } from 'lucide-react'

// Custom components
import BottomNavigation from '../components/BottomNavigation'
import { useAssetData } from '../hooks/useAssetData'
import ErrorView from '../components/ErrorView'
import LoadingView from '../components/LoadingView'
import { compressImage, fileToBase64, getFileMetadata } from '../utils/fileUtils'

import { IWizardQuestion } from '@/types/structures'
import Header from '@/components/Header'
import { useTranslation } from '../hooks/useTranslation'
import { useUserType } from '@/hooks/useUserType'
import { filterVisibleSectionsRecursive, filterVisibleWizardQuestions } from '@/utils/visibility'
import { getAssetValue } from '@/utils/assetValues'

interface WizardState {
  currentQuestionIndex: number
  answers: Record<string, string | string[]>
  isCompleted: boolean
}

interface StagedFiles {
  [questionId: string]: File | File[]
}

const WizardPage = () => {
  const navigate = useNavigate()
  const { uuid, sectionId } = useParams<{ uuid: string; sectionId: string }>()
  const [searchParams] = useSearchParams()
  const { asset: data, isLoading, error: hookError, uuid: resolvedUuid, service } = useAssetData(uuid)
  const { t } = useTranslation()
  const { userType } = useUserType()
  const [availableWizards, setAvailableWizards] = useState<{ id: string; title: string }[]>([])
  const [selectedWizard, setSelectedWizard] = useState<string | null>(sectionId || null)
  const [questions, setQuestions] = useState<IWizardQuestion[]>([])
  const [error, setError] = useState<string | null>(null)
  const [stagedFiles, setStagedFiles] = useState<StagedFiles>({})
  const [previewUrls, setPreviewUrls] = useState<Record<string, string | string[]>>({})
  const [isUploading, setIsUploading] = useState(false)

  const [saving, setSaving] = useState(false)

  // Get showChoosePhoto query parameter
  const showChoosePhoto = searchParams.get('showChoosePhoto') === 'true'

  // Effect to find all available wizard sections and load questions when data is loaded
  useEffect(() => {
    if (data && data.nodes) {
      // Filter visible wizard sections based on user type
      const visibleNodes = filterVisibleSectionsRecursive(data.nodes, userType)
      const wizards = Object.entries(visibleNodes)
        .filter(([_, section]) => section.isWizard)
        .map(([key, section]) => ({ id: key, title: section.title }))

      setAvailableWizards(wizards)

      // If no wizard is selected and we have wizards available, select the first one
      if (!selectedWizard && wizards.length > 0) {
        setSelectedWizard(wizards[0].id)
      }

      // Load questions for the selected wizard
      if (selectedWizard && data.nodes[selectedWizard]?.isWizard) {
        const wizardNode = data.nodes[selectedWizard]
        const allQuestions = wizardNode.questions || []
        // Filter visible questions based on user type
        const visibleQuestions = filterVisibleWizardQuestions(allQuestions, userType)
        setQuestions(visibleQuestions)
        setError(null)
      }
    }
  }, [data, selectedWizard, userType])

  const storageKey = `window_installation_wizard_${uuid}_${selectedWizard}`

  const [state, setState] = useState<WizardState>(() => {
    const saved = localStorage.getItem(storageKey)
    return saved
      ? JSON.parse(saved)
      : {
          currentQuestionIndex: 0,
          answers: {},
          isCompleted: false
        }
  })

  const processKey = (key: string): string => {
    // Remove the #/values/ prefix and replace / with .
    return key.replace('#/values/', '').replace(/\//g, '.')
  }

  const handleFileUpload = async (file: File): Promise<string> => {
    if (!service) {
      throw new Error('Service not available for file upload')
    }
    
    try {
      const prepared = await compressImage(file)
      const base64Data = await fileToBase64(prepared)
      const metadata = getFileMetadata(prepared)
      const result = await service.uploadFile(resolvedUuid, base64Data, metadata)
      const fileIdMatch = (result as string).match(/file-(\w+)/)
      const fileId = fileIdMatch?.[0]
      if (!fileId) {
        throw new Error('Failed to extract file ID from response')
      }
      return fileId
    } catch (error) {
      console.error('Error uploading file:', error)
      throw error
    }
  }

  const handlePhotoCapture = async (event: React.ChangeEvent<HTMLInputElement>, multiple: boolean) => {
    if (!currentQuestion) return
    const files = event.target.files
    if (!files) return

    const newFiles = Array.from(files)
    setStagedFiles(prev => ({
      ...prev,
      [currentQuestion.id]: multiple ? newFiles : newFiles[0]
    }))

    const urls = newFiles.map(file => URL.createObjectURL(file))
    setPreviewUrls(prev => ({
      ...prev,
      [currentQuestion.id]: multiple ? urls : urls[0]
    }))
  }

  const handleNextClick = async () => {
    let uploadedAnswer: string | string[] | null = null

    if (stagedFiles[currentQuestion.id]) {
      const filesToUpload = stagedFiles[currentQuestion.id]
      try {
        setIsUploading(true)
        if (Array.isArray(filesToUpload)) {
          const fileIds = await Promise.all(filesToUpload.map(file => handleFileUpload(file)))
          uploadedAnswer = fileIds
          handleAnswer(fileIds)
        } else {
          const fileId = await handleFileUpload(filesToUpload)
          uploadedAnswer = fileId
          handleAnswer(fileId)
        }
      } catch (err) {
        // Error messages coming from the API layer are translation keys
        setError(err instanceof Error ? t(err.message) : t('WIZARD_UPLOAD_FAILED'))
        setIsUploading(false)
        return
      } finally {
        setIsUploading(false)
      }
    }

    if (state.currentQuestionIndex === questions.length - 1) {
      handleWizardCompletion(uploadedAnswer)
      return
    }
    setState(prev => ({
      ...prev,
      currentQuestionIndex: Math.min(prev.currentQuestionIndex + 1, questions.length - 1)
    }))
  }

  const handleWizardCompletion = async (lastQuestionAnswer?: string | string[] | null) => {
    try {
      setSaving(true) // Start saving

      // Create a copy of answers with the last question answer if provided
      const answersToProcess = { ...state.answers }
      if (lastQuestionAnswer !== null && lastQuestionAnswer !== undefined && currentQuestion) {
        answersToProcess[currentQuestion.id] = lastQuestionAnswer
      }

      // Process each answer and submit it
      const submissions = Object.entries(answersToProcess).map(([questionId, answer]) => {
        // Find the corresponding question to get the refId
        const question = questions.find(q => q.id === questionId)
        if (!question?.refId) return null

        // Process the key
        const processedKey = processKey(question.refId)

        // Handle different types of answers
        let processedValue = ''
        if (Array.isArray(answer)) {
          // For multiselect or photo arrays, join with commas
          processedValue = answer.join(',')
        } else if (typeof answer === 'string') {
          processedValue = answer
        }

        return { key: processedKey, value: processedValue }
      })

      // Filter out null values and submit each answer
      for (const submission of submissions.filter(Boolean)) {
        if (submission && service) {
          await service.updateValue(resolvedUuid, submission.key, submission.value, true)
        }
      }

      // Set completion state and navigate
      setState(prev => ({ ...prev, isCompleted: true }))
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to submit answers')
    } finally {
      setSaving(false)
    }
  }

  // Reset state when changing wizards
  useEffect(() => {
    if (selectedWizard) {
      const saved = localStorage.getItem(storageKey)
      setState(
        saved
          ? JSON.parse(saved)
          : {
              currentQuestionIndex: 0,
              answers: {},
              isCompleted: false
            }
      )
    }
  }, [selectedWizard, storageKey])

  useEffect(() => {
    if (state.isCompleted) {
      navigate(`/go?SERIALNO=${uuid}`)
    }
    if (selectedWizard) {
      localStorage.setItem(storageKey, JSON.stringify(state))
    }
  }, [state, navigate, storageKey, uuid, selectedWizard])

  if (saving) {
    return <LoadingView message={t('WIZARD_SAVING_ANSWERS')} />
  }

  if (isLoading && !data) {
    return <LoadingView message={t('WIZARD_LOADING')} />
  }

  if (hookError) {
    return <ErrorView message={hookError} />
  }

  if (error) {
    return <ErrorView message={error} />
  }

  const currentQuestion = questions[state.currentQuestionIndex]

  const handlePrevious = () => {
    setState(prev => ({
      ...prev,
      currentQuestionIndex: Math.max(0, prev.currentQuestionIndex - 1)
    }))
  }

  const handleAnswer = (value: string | string[]) => {
    setState(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [currentQuestion.id]: value
      }
    }))
  }

  const renderQuestion = () => {
    if (!currentQuestion) return null

    return (
      <div className='w-full max-w-lg'>
          <h2 className='text-xl font-bold text-[hsl(var(--foreground))] mb-2'>
            {t(currentQuestion.question) || t('WIZARD_ANSWER_QUESTION')}
          </h2>
          {(() => {
            switch (currentQuestion.type) {
              case 'text':
                return (
                  <div className='w-full'>
                    <label className='mb-2'>
                      <span className='text-sm text-[hsl(var(--foreground))]'>{t(currentQuestion.question) || t('WIZARD_YOUR_ANSWER')}</span>
                    </label>
                    <input
                      type='text'
                      placeholder={t('WIZARD_ENTER_ANSWER')}
                      className='flex h-12 w-full rounded-[var(--radius)] border border-[hsl(var(--input))] bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]'
                      value={state.answers[currentQuestion.id] || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAnswer(e.target.value)}
                    />
                  </div>
                )

              case 'select':
                return (
                  <div className='w-full'>
                    <label className='mb-2'>
                      <span className='text-sm text-[hsl(var(--foreground))]'>{t(currentQuestion.question) || t('WIZARD_SELECT_OPTION')}</span>
                    </label>
                    <select
                      className='flex h-12 w-full rounded-[var(--radius)] border border-[hsl(var(--input))] bg-transparent px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] appearance-none'
                      value={
                        typeof state.answers[currentQuestion.id] === 'string'
                          ? (state.answers[currentQuestion.id] as string)
                          : ''
                      }
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleAnswer(e.target.value)}
                    >
                      <option disabled value=''>
                        {t('WIZARD_SELECT_OPTION')}
                      </option>
                      {currentQuestion.options?.map(option => (
                        <option key={option} value={option}>
                          {t(option)}
                        </option>
                      ))}
                    </select>
                  </div>
                )

              case 'multiselect':
                return (
                  <div className='w-full'>
                    <label className='mb-2'>
                      <span className='text-sm text-[hsl(var(--foreground))]'>{t(currentQuestion.question) || t('WIZARD_SELECT_OPTIONS')}</span>
                    </label>
                    <div className='flex flex-col space-y-2'>
                      {currentQuestion.options?.map(option => (
                        <label key={option} className='flex items-center gap-3 cursor-pointer py-2'>
                          <input
                            type='checkbox'
                            className='h-4 w-4 rounded border border-[hsl(var(--input))] accent-[hsl(var(--primary))]'
                            checked={
                              Array.isArray(state.answers[currentQuestion.id]) &&
                              (state.answers[currentQuestion.id] as string[]).includes(option)
                            }
                            onChange={e => {
                              const currentAnswers = (state.answers[currentQuestion.id] as string[]) || []
                              if (e.target.checked) {
                                handleAnswer([...currentAnswers, option])
                              } else {
                                handleAnswer(currentAnswers.filter(ans => ans !== option))
                              }
                            }}
                          />
                          <span className='text-sm text-[hsl(var(--foreground))]'>{t(option)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )

              case 'photo':
                return (
                  <div>
                    <input
                      type='file'
                      accept='image/*'
                      id='photo-upload'
                      className='hidden'
                      onChange={e => handlePhotoCapture(e, false)}
                    />
                    <input
                      type='file'
                      accept='image/*'
                      capture='environment'
                      id='camera-upload'
                      className='hidden'
                      onChange={e => handlePhotoCapture(e, false)}
                    />
                    <div className='flex flex-wrap gap-2 mb-3'>
                      <button
                        className='flex-1 h-12 rounded-[var(--radius)] bg-[hsl(var(--primary))] text-white text-sm font-semibold inline-flex items-center justify-center gap-2'
                        onClick={() => document.getElementById('camera-upload')?.click()}
                      >
                        <Camera className='h-4 w-4' />
                        {t('WIZARD_TAKE_PHOTO')}
                      </button>
                      {showChoosePhoto && (
                        <label htmlFor='photo-upload'>
                          <button
                            className='h-12 px-4 rounded-[var(--radius)] border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm font-medium inline-flex items-center gap-2'
                            onClick={() => document.getElementById('photo-upload')?.click()}
                          >
                            <ImagePlus className='h-4 w-4' />
                            {t('WIZARD_CHOOSE_PHOTO')}
                          </button>
                        </label>
                      )}
                    </div>

                    <div className={`mt-2 p-2 bg-[hsl(var(--muted))] rounded ${previewUrls[currentQuestion.id] ? '' : 'hidden'}`}>
                      {previewUrls[currentQuestion.id] && (
                        <>
                          <img
                            src={previewUrls[currentQuestion.id] as string}
                            alt='Preview'
                            className='max-w-full h-auto rounded'
                          />
                          <button
                            className='absolute top-1 right-1 p-1 rounded-full bg-[hsl(var(--destructive))]/90 text-white'
                            onClick={() => {
                              const newStagedFiles = { ...stagedFiles }
                              delete newStagedFiles[currentQuestion.id]
                              setStagedFiles(newStagedFiles)

                              const newPreviewUrls = { ...previewUrls }
                              delete newPreviewUrls[currentQuestion.id]
                              setPreviewUrls(newPreviewUrls)
                            }}
                          >
                            <Trash className='h-3 w-3' />
                            {t('WIZARD_REMOVE')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )

              case 'multiphoto':
                return (
                  <div>
                    <input
                      type='file'
                      accept='image/*'
                      multiple
                      id='multi-photo-upload'
                      className='hidden'
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePhotoCapture(e, true)}
                    />
                    <input
                      type='file'
                      accept='image/*'
                      multiple
                      capture='environment'
                      id='multi-camera-upload'
                      className='hidden'
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePhotoCapture(e, true)}
                    />
                    <div className='flex flex-wrap gap-2 mb-3'>
                      <button
                        className='flex-1 h-12 rounded-[var(--radius)] bg-[hsl(var(--primary))] text-white text-sm font-semibold inline-flex items-center justify-center gap-2'
                        onClick={() => document.getElementById('multi-camera-upload')?.click()}
                      >
                        <Camera className='h-4 w-4' />
                        {t('WIZARD_TAKE_PHOTOS')}
                      </button>
                      {showChoosePhoto && (
                        <label htmlFor='multi-photo-upload'>
                          <button
                            className='h-12 px-4 rounded-[var(--radius)] border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm font-medium inline-flex items-center gap-2'
                            onClick={() => document.getElementById('multi-photo-upload')?.click()}
                          >
                            <ImagePlus className='h-4 w-4' />
                            {t('WIZARD_CHOOSE_PHOTOS')}
                          </button>
                        </label>
                      )}
                    </div>

                    <div className={`mt-3 p-3 bg-[hsl(var(--muted))] rounded-lg ${
                      Array.isArray(previewUrls[currentQuestion.id]) && 
                      (previewUrls[currentQuestion.id] as string[]).length > 0 ? '' : 'hidden'
                    }`}>
                      {Array.isArray(previewUrls[currentQuestion.id]) &&
                        (previewUrls[currentQuestion.id] as string[]).length > 0 && (
                          <>
                            <p className='text-sm text-[hsl(var(--muted-foreground))]'>
                              {(previewUrls[currentQuestion.id] as string[]).length} {t('WIZARD_PHOTOS_STAGED')}
                            </p>
                            <div className='mt-2 flex overflow-x-auto gap-2'>
                              {(previewUrls[currentQuestion.id] as string[]).map((url: string, index: number) => (
                                <div key={`preview-${currentQuestion.id}-${index}`} className='relative flex-shrink-0'>
                                  <img
                                    src={url}
                                    alt={`Preview ${index + 1}`}
                                    className='w-32 h-32 object-cover rounded'
                                  />
                                  <button
                                    className='absolute top-1 right-1 p-1 rounded-full bg-[hsl(var(--destructive))]/90 text-white'
                                    onClick={() => {
                                      const newStagedFiles = [...(stagedFiles[currentQuestion.id] as File[])]
                                      newStagedFiles.splice(index, 1)
                                      setStagedFiles(prev => ({
                                        ...prev,
                                        [currentQuestion.id]: newStagedFiles
                                      }))

                                      const newPreviewUrls = [...(previewUrls[currentQuestion.id] as string[])]
                                      newPreviewUrls.splice(index, 1)
                                      setPreviewUrls(prev => ({
                                        ...prev,
                                        [currentQuestion.id]: newPreviewUrls
                                      }))
                                    }}
                                  >
                                    <Trash className='h-3 w-3' />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <button
                              className='h-9 px-4 rounded-[var(--radius)] border border-[hsl(var(--border))] text-sm font-medium inline-flex items-center gap-2 hover:bg-[hsl(var(--muted))] transition-colors mt-2'
                              onClick={() => {
                                const newStagedFiles = { ...stagedFiles }
                                delete newStagedFiles[currentQuestion.id]
                                setStagedFiles(newStagedFiles)

                                const newPreviewUrls = { ...previewUrls }
                                delete newPreviewUrls[currentQuestion.id]
                                setPreviewUrls(newPreviewUrls)
                              }}
                            >
                              <Trash className='h-4 w-4' />
                              {t('WIZARD_CLEAR_ALL')}
                            </button>
                          </>
                        )}
                    </div>
                  </div>
                )
              default:
                return null
            }
          })()}
      </div>
    )
  }

  // The installer works several rooms in a row: the header has to say which one
  // he is shooting, otherwise every wizard run looks identical.
  const baseWizardTitle = t((selectedWizard && data?.nodes?.[selectedWizard]?.title) || 'Guida alla posa')
  const positionNumber = getAssetValue(data, '#/values/positionInfo/number')
  const wizardTitle = positionNumber
    ? `${baseWizardTitle} ${t('LINKING_POSITION_NUMBER')} n. ${positionNumber}`
    : baseWizardTitle

  return (
    <div className='min-h-screen bg-[hsl(var(--background))]'>
      {/* Back to the general detail of the window room being installed */}
      <Header title={wizardTitle} backTo={uuid ? `/view/${uuid}` : undefined} showBack />

      <div className='container mx-auto px-4 py-8 flex flex-col items-center justify-center pb-24'>
        {!isLoading && !error && selectedWizard && questions.length > 0 && (
          <>
            <div className="flex items-center justify-center gap-1.5 mb-8">
              {questions.map((_, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${idx <= state.currentQuestionIndex ? 'bg-[var(--brand-red)]' : 'bg-[hsl(var(--border))]'}`} />
                  {idx < questions.length - 1 && (
                    <div className={`w-5 h-0.5 rounded ${idx < state.currentQuestionIndex ? 'bg-[var(--brand-red)]/30' : 'bg-[hsl(var(--border))]'}`} />
                  )}
                </div>
              ))}
            </div>

            <div className='w-full flex items-center justify-center'>
              {renderQuestion()}
            </div>

            <div className='flex justify-between mt-6 w-full max-w-lg'>
              <button className='h-12 px-5 rounded-[var(--radius)] border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50' disabled={state.currentQuestionIndex === 0} onClick={handlePrevious}>
                <ArrowLeft className='h-4 w-4' />
                {t('WIZARD_BACK')}
              </button>

              <button
                className={`${state.currentQuestionIndex === questions.length - 1 ? 'flex-1 h-12 rounded-[var(--radius)] bg-[hsl(var(--success))] text-white text-sm font-semibold inline-flex items-center justify-center gap-2' : 'flex-1 h-12 rounded-[var(--radius)] bg-[hsl(var(--primary))] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50'}`}
                onClick={handleNextClick}
                disabled={isUploading}
              >
                {isUploading && <span className='spinner spinner-sm'></span>}
                {state.currentQuestionIndex === questions.length - 1 ? t('WIZARD_COMPLETE') : t('WIZARD_NEXT')}
                {state.currentQuestionIndex !== questions.length - 1 && <ArrowRight className='h-4 w-4' />}
              </button>
            </div>
          </>
        )}

        {!selectedWizard && availableWizards.length > 0 && (
          <div className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] w-full max-w-lg'>
            <div className='p-6'>
              <h2 className='text-xl font-bold mb-4'>{t('WIZARD_SELECT_WIZARD')}</h2>
              <div className='space-y-2'>
                {availableWizards.map(wizard => (
                  <button
                    key={wizard.id}
                    className='w-full h-12 rounded-[var(--radius)] border border-[hsl(var(--border))] text-sm font-medium hover:bg-[hsl(var(--muted))] transition-colors'
                    onClick={() => setSelectedWizard(wizard.id)}
                  >
                    {t(wizard.title)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  )
}

export default WizardPage
